import React, { useCallback, useEffect, useState, ReactNode, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components/macro';
import { useUpdateClinicSettingsConversationsSmsAutoResponseMutation } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Button } from '@televet/kibble-ui';

interface IAutoResponseTextAreaProps {
  name: string;
  description?: ReactNode;
  label: string;
  required: boolean;
  disabled: boolean;
  persistedValue: string | undefined | null;
  isLoading: boolean;
  onOOOResponseChange: (value: boolean) => void;
}

export type SaveOOOResponse = {
  onSaveOOOResponse: () => void;
  onSetInitialData: () => void;
};

const AutoResponseTextArea = forwardRef(
  (
    {
      name,
      label,
      description,
      persistedValue,
      required,
      disabled,
      isLoading,
      onOOOResponseChange,
      ...rest
    }: IAutoResponseTextAreaProps,
    ref,
  ): JSX.Element => {
    // custom hooks
    const { currentClinic } = useClinicUser();

    // page state
    const [draftText, setDraftText] = useState('');
    const [validationMessage, setValidationMessage] = useState('');
    const [wasTextareaFocused, setWasTextareaFocused] = useState(false);

    const [updateSmsAutoResponse] = useUpdateClinicSettingsConversationsSmsAutoResponseMutation();

    const defaultResponse = useCallback(
      (name) => {
        const defaultResponses: { [key: string]: string } = {
          smsAutoResponseWhenOpen: `Thanks for contacting ${
            currentClinic?.name || 'our clinic'
          }. Our team will be reaching out shortly. In the meantime, feel free to reply with any additional details or questions so we may best assist you.`,
          smsAutoResponseWhenClosed: `Thanks for contacting ${
            currentClinic?.name || 'our clinic'
          }. Our office is currently closed, but we will get back to you during normal business hours, starting {{next open day}} at {{time}}. In the meantime, feel free to reply with any additional details or questions so we may best assist you.`,
        };
        return defaultResponses[name];
      },
      [currentClinic],
    );

    const setInitialData = useCallback(() => {
      if (!isLoading) {
        persistedValue === null || persistedValue === undefined
          ? setDraftText(defaultResponse(name))
          : setDraftText(persistedValue);
        setWasTextareaFocused(false);
        setValidationMessage('');
      }
    }, [defaultResponse, isLoading, name, persistedValue]);

    const isDirtyInput = useCallback(
      (value: string) => {
        let hasResponseChanged = false;
        if (persistedValue === null || persistedValue === undefined) {
          hasResponseChanged = wasTextareaFocused && value !== defaultResponse(name);
        } else if (value === '') {
          hasResponseChanged = wasTextareaFocused;
        } else {
          hasResponseChanged = wasTextareaFocused && value !== persistedValue;
        }
        return hasResponseChanged;
      },
      [persistedValue, wasTextareaFocused, defaultResponse, name],
    );

    const handleDraftTextChange = (value: string): void => {
      if (validationMessage && value) {
        setValidationMessage('');
      }
      setDraftText(value);
      if (isDirtyInput(value)) {
        onOOOResponseChange(true);
      } else {
        onOOOResponseChange(false);
      }
    };

    useEffect(() => {
      setInitialData();
    }, [setInitialData]);

    useImperativeHandle(ref, () => ({
      onSaveOOOResponse(): void {
        if (required && !draftText) {
          setValidationMessage('Please provide a response');
          return;
        }
        const savedText = draftText === defaultResponse(name) ? null : draftText;

        updateSmsAutoResponse({
          variables: {
            where: { id: currentClinic?.clinicSetting?.id },
            data: { [name]: savedText },
          },
        });
        setWasTextareaFocused(false);
      },
      onSetInitialData(): void {
        setDraftText('');
        setInitialData();
      },
    }));

    return (
      <AutoResponseInputContainer>
        {label && (
          <Text size="sm" fontWeight="bold">
            {label}
          </Text>
        )}
        {description && <Description>{description}</Description>}
        <StyledTextArea
          isDisabled={disabled}
          name={name}
          rows={4}
          value={draftText}
          size="sm"
          borderRadius="7px"
          lineHeight="21px"
          onFocus={(): void => setWasTextareaFocused(true)}
          onChange={(e): void => handleDraftTextChange(e.target.value)}
          {...rest}
        />
        {validationMessage && <ValidationMessage>{validationMessage}</ValidationMessage>}
        {isDirtyInput(draftText) && !isLoading && (
          <ActionsContainer>
            <div>
              <Button
                onClick={(): void => {
                  handleDraftTextChange(defaultResponse(name));
                  setWasTextareaFocused(false);
                }}
              >
                Reset to default
              </Button>
            </div>
          </ActionsContainer>
        )}
      </AutoResponseInputContainer>
    );
  },
);

AutoResponseTextArea.displayName = 'AutoResponseTextArea';

const StyledTextArea = styled(Textarea)``;

export const Description = styled.div`
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const AutoResponseInputContainer = styled.div`
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${StyledTextArea} {
    width: 500px;
  }

  ${Description} {
    width: 500px;
  }
`;

const ValidationMessage = styled.div`
  color: var(--chakra-colors-text-danger);
  font-size: 14px;
  margin: 10px 0;
`;

const ActionsContainer = styled.div`
  width: 500px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  *:first-child {
    flex: 1 1 auto;
  }
`;

export default AutoResponseTextArea;
